import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Scale, ChevronDown, ChevronRight } from "lucide-react";
import { RemoveFromPayrollDialog } from "./RemoveFromPayrollDialog";
import { DeleteEmployeeDialog } from "./DeleteEmployeeDialog";

const TONE = { UNCHANGED: "bg-slate-100 text-slate-700", CHANGED: "bg-amber-100 text-amber-900",
  NEW: "bg-emerald-100 text-emerald-800", NEW_DRAFT: "bg-sky-100 text-sky-800", REMOVED: "bg-red-100 text-red-800" };
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

function Checks({ c }) {
  const items = [["Duplicate Employee IDs", c.duplicate_employee_ids.length], ["Same-day attendance conflicts", c.attendance_conflicts.length],
    ["Name/ID issues", c.identity_issues.length], ["Missing salary structure", c.missing_salary.length],
    ["Not in Employee Master (review)", c.needs_review.length], ["Excluded employees", c.excluded.length],
    ["Centre allocation mismatches", c.allocation_mismatch.length], ["Centres with attendance not locked", c.unlocked_centres.length]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="recon-checks">
      {items.map(([l, n]) => (
        <div key={l} className={`border rounded p-2 ${n ? "border-amber-300 bg-amber-50" : ""}`}>
          <div className="text-muted-foreground">{l}</div><div className="text-lg font-semibold">{n}</div></div>))}
    </div>
  );
}

export function PayrollReconciliationPanel({ session, month }) {
  const token = session.token;
  const isSA = !!session.is_super_admin;
  const [rm, setRm] = useState(null);
  const [del, setDel] = useState(null);
  const [open, setOpen] = useState({});
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [elig, setElig] = useState(null);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) {
    toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const compare = () => run(async () => setData((await api.post("/payroll_reconciliation_v2", { token, month })).data));
  const approve = () => run(async () => {
    await api.post("/payroll_snapshot_regenerate", { token, month });
    toast.success("Changes approved — draft updated"); await compare();
  });
  const finalise = () => run(async () => {
    if (!window.confirm(`Finalise & lock payroll ${month}? A new version is created and kept permanently.`)) return;
    const r = await api.post("/payroll_snapshot_finalise", { token, month, approve_changes: true });
    toast.success(`Payroll ${month} finalised — Version ${r.data.version}`); await compare();
  });
  const reopen = () => run(async () => {
    const reason = window.prompt("Reason for reopening payroll (creates a new revision):");
    if (!reason || reason.trim().length < 3) return;
    await api.post("/payroll_snapshot_unlock", { token, month, reason });
    toast.success("Payroll reopened as a new revision"); await compare();
  });
  const eligibility = () => run(async () => setElig((await api.post("/payroll_eligibility_review", { token, month })).data));

  const lines = (data?.lines || []).filter((l) => !onlyDiff || l.status !== "UNCHANGED");
  return (
    <Card data-testid="payroll-recon-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2"><Scale className="w-4 h-4" />
          Attendance &amp; Payroll Reconciliation · {month}
          {data && <Badge variant="outline" data-testid="recon-state">{data.state}{data.version ? ` · V${data.version}` : ""}</Badge>}</CardTitle>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={compare} disabled={busy} data-testid="recon-compare-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Compare with Previous Calculation</Button>
          <Button size="sm" variant="outline" onClick={eligibility} disabled={busy} data-testid="recon-eligibility-btn">Review Payroll Employee Eligibility</Button>
          {data && data.state !== "FINAL" && <>
            <Button size="sm" variant="outline" onClick={() => setOnlyDiff(!onlyDiff)} data-testid="recon-review-diff-btn">
              {onlyDiff ? "Show all" : "Review Differences"}</Button>
            <Button size="sm" onClick={approve} disabled={busy} data-testid="recon-approve-btn">Approve Changes</Button>
            <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={finalise} disabled={busy} data-testid="recon-finalise-btn">Finalise &amp; Lock</Button></>}
          {data?.state === "FINAL" && <Button size="sm" variant="outline" onClick={reopen} disabled={busy} data-testid="recon-reopen-btn">Reopen as new revision</Button>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data && <p className="text-sm text-muted-foreground">Click Compare to check the draft against verified attendance. Nothing is saved until you approve.</p>}
        {data?.notice && <div className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800" data-testid="recon-notice">{data.notice}</div>}
        {data && <>
          <div className="text-sm flex flex-wrap gap-4" data-testid="recon-summary">
            <span>Changes since previous: <b>{data.changes_since_previous}</b></span>
            <span>Live gross: <b>{fmt(data.totals_live.gross)}</b></span>
            <span>Saved gross: <b>{fmt(data.totals_stored?.gross)}</b></span>
            <span className="text-muted-foreground">Versions: {data.versions.map((v) => `V${v.version} (${(v.finalized_at || "").slice(0, 10)})`).join(", ") || "none"}</span>
          </div>
          <Checks c={data.checks} />
          <div className="border rounded overflow-auto max-h-[60vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 text-left"><tr>
                {["Employee ID", "Employee Name", "Designation", "Centre", "Monthly Salary", "Attendance Days", "Payable Days",
                  "Gross Salary", "Advance", "Net Salary", "Status", "Change", "Difference", "Actions"].map((h) =>
                  <th key={h} className="p-1.5 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>{lines.map((l) => {
                const split = (l.centreAllocation || []).length > 1;
                const missing = l.salary_rate_missing;
                return [
                  <tr key={l.employeeId} className={`border-t align-top ${missing ? "bg-amber-50" : ""}`} data-testid={`recon-row-${l.employeeId}`}>
                    <td className="p-1.5 text-muted-foreground">{l.employeeId}</td>
                    <td className="p-1.5 font-medium">{l.employeeName}</td>
                    <td className="p-1.5">{l.designation}</td>
                    <td className="p-1.5">{split ? (
                      <button className="flex items-center gap-1 text-blue-700" onClick={() => setOpen({ ...open, [l.employeeId]: !open[l.employeeId] })}
                        data-testid={`recon-split-${l.employeeId}`}>
                        {open[l.employeeId] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {l.centreAllocation.map((a) => a.center).join(" + ")}</button>) : (l.centreAllocation?.[0]?.center || l.homeCenter)}</td>
                    <td className="p-1.5">{missing ? <Badge className="bg-amber-200 text-amber-900">SALARY RATE MISSING</Badge> : fmt(l.monthlySalary)}</td>
                    <td className="p-1.5">{l.attendanceDays ?? "—"}</td>
                    <td className="p-1.5">{l.payableDays}</td>
                    <td className="p-1.5">{missing ? "—" : fmt(l.salary)}</td>
                    <td className="p-1.5">{fmt(l.advance)}</td>
                    <td className="p-1.5 font-semibold">{missing ? "—" : fmt(l.net)}</td>
                    <td className="p-1.5"><Badge variant="outline">{data.state === "FINAL" ? `LOCKED V${data.version}` : (l.rowStatus || "DRAFT")}</Badge></td>
                    <td className="p-1.5"><Badge className={TONE[l.status]}>{l.status}</Badge></td>
                    <td className="p-1.5">{fmt(l.difference)}</td>
                    <td className="p-1.5 whitespace-nowrap">{isSA && l.status !== "REMOVED" && <>
                      <Button size="sm" variant="outline" className="h-6 px-2" disabled={data.state === "FINAL"}
                        title={data.state === "FINAL" ? "Reopen payroll as a new revision first" : ""}
                        onClick={() => setRm({ employeeId: l.employeeId, employeeName: l.employeeName, center: "ALL",
                          gross: l.salary, net: l.net, payableDays: l.payableDays })} data-testid={`recon-remove-${l.employeeId}`}>Remove from Payroll</Button>
                      {!l.employeeId.startsWith("NAME:") && <Button size="sm" variant="ghost" className="h-6 px-2 text-red-700"
                        onClick={() => setDel({ employee_id: l.employeeId, name: l.employeeName })} data-testid={`recon-delete-${l.employeeId}`}>Delete from Master</Button>}</>}</td>
                  </tr>,
                  split && open[l.employeeId] && l.centreAllocation.map((a) => (
                    <tr key={`${l.employeeId}-${a.center}`} className="bg-slate-50 text-[11px]" data-testid={`recon-alloc-${l.employeeId}-${a.center}`}>
                      <td /><td className="p-1 pl-4" colSpan={2}>↳ centre share</td><td className="p-1">{a.center}</td><td />
                      <td className="p-1">{a.markedDays}</td><td className="p-1">{a.payableDays}</td><td className="p-1">{fmt(a.grossSalary)}</td>
                      <td className="p-1">{fmt(a.advance)}</td><td className="p-1">{fmt(a.netSalary)}</td><td colSpan={3} />
                      <td className="p-1">{isSA && data.state !== "FINAL" && <Button size="sm" variant="ghost" className="h-5 px-2"
                        onClick={() => setRm({ employeeId: l.employeeId, employeeName: l.employeeName, center: a.center,
                          gross: a.grossSalary, net: a.netSalary, payableDays: a.payableDays })}
                        data-testid={`recon-remove-${l.employeeId}-${a.center}`}>Remove {a.center} share</Button>}</td>
                    </tr>))];
              })}</tbody>
            </table>
          </div></>}
        {elig && (
          <div className="border rounded p-2 text-xs space-y-1" data-testid="recon-eligibility">
            <div className="font-semibold">Excluded ({elig.excluded.length})</div>
            {elig.excluded.map((x) => <div key={x.employeeId}>{x.employeeName} · {x.center} — {x.reason}</div>)}
            <div className="font-semibold mt-2">Included needing review ({elig.included.filter((x) => x.needs_review).length})</div>
            {elig.included.filter((x) => x.needs_review).map((x) => <div key={x.employeeId}>{x.employeeName} — {x.reason}</div>)}
          </div>)}
      </CardContent>
      <RemoveFromPayrollDialog open={!!rm} onClose={() => setRm(null)} token={token} month={month} target={rm} onDone={compare} />
      <DeleteEmployeeDialog open={!!del} onClose={() => setDel(null)} session={session} employee={del} onDeleted={compare} />
    </Card>
  );
}
